// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import { Flex, useMobileMediaQuery } from '@iheartjane/reefer';
import type { AppMode, Breadcrumb, PriceId } from '@iheartjane/types';
import type { ReactNode } from 'react';
import { useCallback } from 'react';
import { Link, useParams } from 'react-router';

import type { AlgoliaProduct, JaneSearchState } from '@jane/search/types';
// eslint-disable-next-line no-restricted-imports
import type {
  Brand,
  MenuProduct,
  PendingCartProduct,
  Product,
  ReservationCartProduct,
  Store,
} from '@jane/shared/models';
import type {
  EcommPaths,
  MenuProduct as ZMenuProduct,
} from '@jane/shared/types';
import type { Categories } from '@jane/shared/util';
import { monolithPaths, postMessageToIframeParent } from '@jane/shared/util';

export const MENU_PRODUCT_ITEM_HEIGHT = 475;
export const MENU_PRODUCT_MOBILE_ITEM_HEIGHT = 425;
export const MENU_PRODUCT_SPONSORED_HEIGHT = 32;

export const MENU_PRODUCT_VB_DESCRIPTION_ITEM_HEIGHT = 575;
export const MENU_PRODUCT_VB_DESCRIPTION_ITEM_MOBILE_HEIGHT = 500;
export const DEFAULT_DESKTOP_VB_DESCRIPTION_CARD_WIDTH = 243;

export const DEFAULT_MOBILE_CAROUSEL_CARD_WIDTH = 165;
export const DEFAULT_MOBILE_CARD_WIDTH = 150;
export const DEFAULT_DESKTOP_CARD_WIDTH = 200;
export const DEFAULT_MOBILE_GRID_GAP = 16;
export const DEFAULT_DESKTOP_GRID_GAP = 24;

interface PriceLookUp {
  [key: string]: string;
}

export type ProductCardMode = 'default' | 'storeMenu';

export const PRICE_LOOKUP: PriceLookUp = {
  HALF_GRAM: '.5g',
  GRAM: '1g',
  TWO_GRAM: '2g',
  EIGHTH_OUNCE: '3.5g',
  QUARTER_OUNCE: '7g',
  HALF_OUNCE: '14g',
  OUNCE: '28g',
};

export const ItemContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
  position: 'relative',
  transition: 'box-shadow .3s',
  ':focus-visible': {
    boxShadow: '0 2px 11px 0 rgba(108, 108, 108, 0.5)',
    outline: 'none',
  },
});

interface StyledElementType {
  appMode: AppMode;
  baseCardHeight: number;
  height?: string | number;
}

const StyledLink = styled(Link, {
  shouldForwardProp: (propName: string) =>
    propName !== 'appMode' && propName !== 'baseCardHeight',
})<StyledElementType>(({ height, appMode, baseCardHeight, theme }) => ({
  position: 'relative',
  width: '100%',
  height,
  maxHeight: baseCardHeight,
  pointerEvents: appMode === 'headless' ? 'none' : undefined,
  ':focus-visible': {
    outline: 'none',
  },
  ':focus-visible:before': {
    backgroundColor: theme.colors.grays.hover,
    borderRadius: 'inherit',
    content: '""',
    height: '100%',
    position: 'absolute',
    width: '100%',
    zIndex: '1',
  },
}));

const StyledFlex = styled(Flex, {
  shouldForwardProp: (propName: string) =>
    propName !== 'appMode' && propName !== 'baseCardHeight',
})<StyledElementType>(({ height, appMode, baseCardHeight, theme }) => ({
  position: 'relative',
  width: '100%',
  height,
  maxHeight: baseCardHeight,
  pointerEvents: appMode === 'headless' ? 'none' : undefined,
  ':focus-visible': {
    outline: 'none',
  },
  ':focus-visible:before': {
    backgroundColor: theme.colors.grays.hover,
    borderRadius: 'inherit',
    content: '""',
    height: '100%',
    position: 'absolute',
    width: '100%',
    zIndex: '1',
  },
}));

/**
 * Everything `productDetailLink` reads off a product. Structural so any menu,
 * search or cart product satisfies it; `product_id` is optional because only
 * menu products carry it (the `id` fallback below covers the rest).
 */
export interface ProductDetailLinkProduct {
  brand?: string | null;
  id: string | number;
  name?: string;
  product_id?: string | number;
  url_slug?: string | null;
}

export const productDetailLink = ({
  product,
  appMode,
  store,
  brand,
  fromAllSpecials,
  bundleSpecialId,
  fromSpecialId,
}: {
  appMode: AppMode;
  brand?: Pick<Brand, 'id' | 'name'> | boolean;
  bundleSpecialId?: string | number | null;
  fromAllSpecials?: boolean | null;
  fromSpecialId?: string | number | null;
  product: ProductDetailLinkProduct;
  store?: Pick<Store, 'id' | 'name'>;
}) => {
  const slug = product.url_slug ?? undefined;
  const productNameAndSlugAndId = {
    id: product.product_id || product.id,
    name: product.name,
    slug,
    brand: product.brand,
  };

  if (appMode === 'default') {
    if (store) {
      return monolithPaths.storeProduct({
        store,
        product: productNameAndSlugAndId,
        fromAllSpecials,
        bundleSpecialId,
        fromSpecialId,
      });
    }
    if (brand) {
      return monolithPaths.brandProduct({
        brand: brand as Brand,
        product: productNameAndSlugAndId,
        bundleSpecialId,
      });
    }
    return monolithPaths.product({
      product: productNameAndSlugAndId,
      slug,
      bundleSpecialId,
    });
  }

  if (['bloom', 'headless'].includes(appMode)) {
    return monolithPaths.headlessProductDetail(productNameAndSlugAndId);
  }

  return monolithPaths.embeddedProductDetail({
    product: productNameAndSlugAndId,
    fromAllSpecials,
    bundleSpecialId,
    fromSpecialId,
  });
};

export interface LinkProps {
  appMode: AppMode;
  brand?: Brand;
  breadcrumbs?: Breadcrumb[];
  bundleSpecialId?: string | number | null;
  cartIsOpen: boolean;
  children: ReactNode;
  defaultWeight?: PriceId;
  fromAllSpecials?: boolean | null;
  fromSpecialId?: string | number | null;
  height?: number | string;
  onClick?: () => void;
  onCloseCart?: () => void;
  pdpPath?: EcommPaths['menuProduct'];
  product: Product;
  searchState: JaneSearchState<AlgoliaProduct> | undefined;
  store?: Store;
  trackListViewClick?: () => void;
  trackProductListClick?: () => void;
}

export const ProductDetailPageLink = ({
  appMode,
  brand,
  breadcrumbs,
  cartIsOpen,
  product,
  searchState,
  children,
  defaultWeight,
  store,
  bundleSpecialId,
  fromAllSpecials,
  fromSpecialId,
  height,
  onCloseCart,
  pdpPath,
  trackListViewClick,
  trackProductListClick,
}: LinkProps) => {
  const { brand_id, brand_name, category, special_id } = useParams<{
    brand_id: string;
    brand_name?: string;
    category?: Categories;
    special_id?: string;
  }>();

  const memoizedOnClick = useCallback(() => {
    if (cartIsOpen) {
      onCloseCart && onCloseCart();
    }
    trackListViewClick && trackListViewClick();
    trackProductListClick && trackProductListClick();
  }, [searchState, cartIsOpen]);

  const isMobile = useMobileMediaQuery({});

  const baseCardHeight = isMobile
    ? MENU_PRODUCT_MOBILE_ITEM_HEIGHT
    : MENU_PRODUCT_ITEM_HEIGHT;

  const isFromProductBrand = brand_id !== undefined;
  const pdpLink = pdpPath
    ? pdpPath(product, defaultWeight)
    : productDetailLink({
        product,
        appMode,
        store,
        brand: isFromProductBrand ? brand : false,
        fromAllSpecials,
        bundleSpecialId,
        fromSpecialId,
      });

  const locationState = {
    ...(searchState ? { searchState } : {}),
    ...(category ? { fromCategory: category } : {}),
    ...(breadcrumbs ? { breadcrumbs } : {}),
    ...(special_id && brand_name && brand_id
      ? { fromBrandSpecialId: special_id }
      : {}),
  };

  if (['bloom', 'headless'].includes(appMode)) {
    return (
      <StyledFlex
        appMode={appMode}
        baseCardHeight={baseCardHeight}
        height={height}
        onClick={() => {
          postMessageToIframeParent({
            messageType: 'navigationEvent',
            payload: {
              name: 'navigateToProduct',
              link: pdpLink,
            },
          });
        }}
        role="link"
      >
        {children}
      </StyledFlex>
    );
  }

  return (
    <StyledLink
      appMode={appMode}
      baseCardHeight={baseCardHeight}
      height={height}
      to={pdpLink}
      onClick={memoizedOnClick}
      role="link"
      state={locationState}
    >
      {children}
    </StyledLink>
  );
};

export const sortByPriceId = (
  cartProduct: ReservationCartProduct[] | PendingCartProduct[],
  menuProduct: MenuProduct | ZMenuProduct
): ReservationCartProduct[] | PendingCartProduct[] =>
  cartProduct.sort((a, b) =>
    (menuProduct?.available_weights || []).indexOf(
      a.price_id.replace('_', ' ')
    ) >
    (menuProduct?.available_weights || []).indexOf(b.price_id.replace('_', ' '))
      ? 1
      : -1
  );
